import * as React from 'react';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  Link,
} from 'react-router-dom';
import {Box} from '@mui/material';
import {useSecretMobileUtilityMenu} from '@practicegenius/pg-ui/src/hooks/useSecretMobileUtilityMenu';
import {postMessageToMobileApp} from '@practicegenius/pg-ui/src/utils/MobileApp';
import {inferServiceBrandFromWindow} from '@practicegenius/domain/src/data/serviceBrand';
import Login from './screens/Login';
import {Home} from './screens/Home';
import {ForgotPassword} from './screens/ForgotPassword';
import {PasswordEmailSent} from './screens/PasswordEmailSent';
import ForgotPasswordReset from './screens/ForgotPasswordReset';
import {PasswordResetSuccess} from './screens/PasswordResetSuccess';
import {ForgotUsername} from './screens/ForgotUsername';
import {UsernameEmailSent} from './screens/UsernameEmailSent';
import {routes} from './routes';
import {LegacyContactVerificationLanding} from './screens/LegacyContactVerificationLanding';
import {UnauthenticatedStandardProvider} from '../../components/StandardProvider';
import {UnauthenticatedCheckIn} from './screens/UnauthenticatedCheckIn';

/**
 * The Home app provides most of the UI for the unauthenticated portion of sites dot. The exceptions are:
 * - account registration
 * - promotions
 *
 * There is not a huge appetite to refactor those apps into this one with the idea of a Next.js migration on the horizon.
 */
export default function App() {
  const utilityMenu = useSecretMobileUtilityMenu({
    practiceInfo: {
      id: window.phpJs.practice.practice_id,
      hubUrl: window.location.origin,
      practiceFeatures: window.phpJs.practiceFeatures,
    },
  });
  const serviceBrand = inferServiceBrandFromWindow();

  // Let the mobile app know that the user is not authenticated
  React.useEffect(() => {
    postMessageToMobileApp({
      type: 'notify_user_authenticated_status',
      payload: {
        authenticated: false,
      },
    });
  }, []);

  return (
    <UnauthenticatedStandardProvider showErrorMessage>
      <Router>
        <Switch>
          <Redirect from="/login/index" exact to={routes.login.index} />
          <Redirect from="/home" exact to={routes.index} />
          {(
            [
              [
                routes.index,
                () => (
                  <Home
                    serviceBrand={serviceBrand}
                    buttonProps={{
                      login: {
                        component: Link,
                        // @ts-expect-error Known issue with OverridableComponent
                        to: {
                          pathname: routes.login.index,
                          state: {showBackButton: true},
                        },
                      },
                      register: {
                        component: 'a',
                        // @ts-expect-error Known issue with OverridableComponent
                        href: '/account/register',
                      },
                    }}
                  />
                ),
              ],
              [routes.checkin, UnauthenticatedCheckIn],
              [routes.login.index, Login],
              [routes.login.forgotUsername, ForgotUsername],
              [routes.login.forgotUsernameEmailSent, UsernameEmailSent],
              [routes.login.forgotPassword, ForgotPassword],
              [routes.login.forgotPasswordEmailSent, PasswordEmailSent],
              [routes.login.resetPassword, ForgotPasswordReset],
              [routes.login.passwordResetSuccess, PasswordResetSuccess],

              // Temporary: A little landing page for legacy contact verification emails.
              [
                routes.login.legacyContactVerification,
                LegacyContactVerificationLanding,
              ],
            ] as [string, React.ComponentType][]
          ).map(([path, Component]) => (
            <Route exact key={path} path={path} component={Component} />
          ))}
          <Redirect to={routes.login.index} />
        </Switch>
      </Router>
      {utilityMenu.displayMenu && (
        <>
          <Box
            sx={{
              position: 'fixed',
              bottom: theme => theme.spacing(2),
              right: theme => theme.spacing(2),
              width: '60px',
              height: '60px',
            }}
            onClick={utilityMenu.incrementTickCount}
          />
          {utilityMenu.dialog}
        </>
      )}
    </UnauthenticatedStandardProvider>
  );
}
